import { normalizePhone } from "../../../common/utils/appUtils";
import { INVALID_DATE } from "../../../common/constants/ApiConstants";

/**
 * Input constraints for the Quick Entry patient/requestor entry fields.
 *
 * Cluster fix for AB#35251 (no character limit on Patient Info / Create New Patient /
 * Requestor Info fields), AB#35253 (Requestor City / State / Zip) and AB#35252 (the
 * Requestor Info Fax SEARCH box accepted letters). One mechanism resolves all three: every
 * constrained field declares its cap here, and every field that restricts a character class
 * declares its kind, so the forms stop being the place where limits and masks are (or
 * aren't) remembered.
 *
 * sanitizeFieldInput is the single entry point - CreateEntityFormModal (create/edit) and
 * InlineSearchForm (the inline search rows) both call it, so a field spelled "phone" behaves
 * the same wherever it appears. Before this consolidation the two forms masked independently,
 * which is exactly how the Fax search box ended up with no mask at all (AB#35252).
 *
 * Pure functions, no React, so the rules are unit-testable without mounting the row
 * components - same seam as intakeFetchGate.ts / serviceDateAllDates.ts.
 *
 * WHERE THE LIMITS COME FROM. Not invented: each is the cap Classic Mode already applies,
 * cross-checked against the column width in the ROIS schema (queried against aspDev), which is
 * what actually truncates or rejects on save. Quick Entry was the only pane with no caps at
 * all, so this is a parity fix, not a new policy.
 *
 *   patientFirstName  40  tblPatientData.FirstName        (Classic NAME_MAX_LENGTH = 40)
 *   patientLastName   40  tblPatientData.LastName         (Classic NAME_MAX_LENGTH = 40)
 *   patientMiddleName 20  tblPatientData.MiddleName       - narrower than the other two
 *   patientMrn        30  tblPatientData.MRN              (Classic MEDICAL_RECORD_NUMBER_MAX_LENGTH = 30)
 *   patientEmpi       30  Classic EMPI_MAX_LENGTH = 30
 *   requestorFax      12  the XXX-XXX-XXXX mask AssignContactModal already uses (column allows 30)
 *   requestorEmail    50  tblRequestorContact.Email
 *   requestorCity     30  Classic CITY maxLength = 30      (RequestorContactPanel.tsx ~625)
 *   requestorState     3  tblRequestorName.State           - see the State note below
 *   requestorZip      10  tblRequestorName.ZipCode varchar(10) - see the Zip note below
 *
 * THE THREE FIELDS AB#35253 REPORTS, against what Classic's REQUESTOR tab actually does
 * (RequestorContactPanel.tsx for the inputs, localValidations.ts validateRequestorTab for the
 * rules). Read this table before citing parity anywhere:
 *
 *   City   Classic: plain text, maxLength 30, no character rule at all.
 *          Here:    maxLength 30 + QUICK_ENTRY_CITY_REGEX - knowingly STRICTER (see below).
 *   State  Classic: dropdown over UNITED_STATES + `state.length < 2` -> "Enter valid state."
 *          Here:    the same dropdown + QUICK_ENTRY_STATE_REGEX - stricter (letters, 2-3).
 *   Zip    Classic: maxLength 5 + `zip.length < 5` -> "Enter valid zip code."
 *          Here:    maxLength 5 (CLASSIC_ENTRY_ZIP_MAX_LENGTH) + CLASSIC_ZIP_REGEX - stricter.
 *
 * CLASSIC_ZIP_REGEX IS NOT CLASSIC'S REQUESTOR-TAB RULE. It is Classic's BILL TO zip rule
 * (localValidations.ts:336 and :353); the requestor tab's own rule is the bare length check above,
 * so "abcde" passes in Classic and is refused here. That divergence arrived with AB#37973, which
 * un-masked this field and gave it the regex; AB#35253 changes only the cap, 10 -> 5. It is kept
 * because the bug asks for a field that refuses letters and specials and reporting is the
 * non-destructive way to do that - but it is a divergence, and must not be cited as parity.
 *
 * WHY CITY IS STRICTER THAN CLASSIC. Two lines are on the work item and no third: QA, 2026-08-17 -
 * "Retested this defect. The issue still persists for the City field"; triage, 2026-08-19 - "Valid
 * bug, should mimic behavior as per requestor tab in classic mode ... will fix as per QA
 * instruction". They pull in opposite directions on City - "mimic Classic" would leave it
 * unrestricted - and the tie is broken the way the triage line breaks it, per QA's instruction. No
 * product decision on City's character class is recorded anywhere on the work item, and this file
 * must not pretend one is. If that reading is wrong, this is the paragraph to argue with.
 *
 * NOTHING IS EVER REWRITTEN. City, State, Zip and Phone all cap length, VALIDATE post-hoc, and
 * report. Two keystroke masks were reverted for destroying data silently: Zip's digits-only mask
 * turned "K1A 0B1" into "101" (AB#37973), and City's strip turned "Wilkes<U+2015>Barre" into
 * "WilkesBarre", which then PASSED the validator with Save enabled. The line for all four fields:
 * an invalid value is reported and gates Save; a valid value nobody typed is kept whole.
 *
 * WHAT THESE VALIDATORS DO NOT REACH - an AUTOMATED SINGLE MATCH. They are field rules, so they
 * cover the two doors a form has (a typed value and a pre-filled one). On a confident automated
 * match no form opens at all and RequestorInfoRow writes the MATCHED RECORD's stored City/State/Zip
 * into the transaction and the send-to mirror directly, unvalidated. A stored invalid value
 * therefore still reaches persistence on that path. Deliberately not gated here - see the note on
 * that effect in RequestorInfoRow.tsx for why, and AB#35253's PR description for the caveat.
 *
 * QUICK_ENTRY_FIELD_MAX_LENGTH.requestorZip stays 10: it is the varchar(10) column width and the
 * cap sanitizeZipInput applies for the search-only boxes, whose 10-char zip Classic likewise
 * allows (SEARCH_ZIP, ~473-479). Classic draws exactly this entry-vs-search distinction, so this
 * file does too. No field declares the `postalCode` kind today - the requestor Zip stopped
 * declaring it in AB#37973 - and the kind is left in place for those search boxes.
 *
 * REQUEST ID DELIBERATELY HAS NO KIND HERE (AB#37972 issue 2 - reverted after review). A digits-
 * and-single-hyphen restriction was added and then reverted: Classic's own Request ID field
 * (DataEntryDetailsRightPane.tsx ~2097-2109) is a plain ReduxFormTextField with no `type`, so it
 * accepts ANY character, and the column itself carries no format constraint - the identifier's
 * shape is chosen by the FACILITY (the field only renders when `facility.useRequestID` is set),
 * not by this application. The "XXXXXX-XXXX" placeholder both panes carry is cosmetic, not a
 * specification; enforcing it would silently corrupt a facility-issued id like "REQ-000123" into
 * "-000123" on the next keystroke. See RequestIdField.tsx - it deliberately does not route
 * through sanitizeFieldInput. Routed to product on AB#37972 as a data-format question.
 */
export const QUICK_ENTRY_FIELD_MAX_LENGTH = {
    patientFirstName: 40,
    patientLastName: 40,
    patientMiddleName: 20,
    patientMrn: 30,
    patientEmpi: 30,
    requestorFax: 12,
    requestorEmail: 50,
    requestorCity: 30,
    requestorState: 3,
    requestorZip: 10,
} as const;
/**
 * AB#39478: caps for the Advanced Search boxes on the requestor row. A SEPARATE constant, for
 * the same reason AB#35253 kept the entry zip separate: these are Classic's own SEARCH-box
 * caps, not schema column widths, and QUICK_ENTRY_FIELD_MAX_LENGTH is locked to the widths by
 * its test. A search box may legitimately differ from the column it queries - it is matching a
 * prefix, not storing a value.
 *
 *   address       70  RequestorContactPanel.tsx:363
 *   zip           10  RequestorContactPanel.tsx:378 (same as the column, kept here for cohesion)
 *   requestorName 50  RequestorContactPanel.tsx:395
 *   contactName   50  RequestorContactPanel.tsx:410
 */
export const REQUESTOR_SEARCH_MAX_LENGTH = {
    address: 70,
    zip: 10,
    requestorName: 50,
    contactName: 50,
} as const;

/**
 * AB#39478: Classic's own two search-validation messages, verbatim (RequestorContactPanel).
 * Shared here (not duplicated as a literal in each pane) so the Quick Entry requestor row and
 * the Classic pane it opens stay in sync by construction, not by convention.
 */
export const BASIC_SEARCH_MIN_MESSAGE = "At least one value is required to search.";
export const ADVANCED_SEARCH_MIN_MESSAGE = "At least two values are required to search.";

/**
 * AB#35253 issue 2 (QA retest 2026-08-17): the requestor ENTRY zip's cap, which is Classic's own
 * `maxLength: 5` (RequestorContactPanel.tsx ~562-575) - NOT the column width above. QA measured the
 * divergence directly: "In Classic mode, the zip code field accepts up to 5 digits, but Quick Entry
 * accepts up to 10."
 *
 * A second constant rather than a change to QUICK_ENTRY_FIELD_MAX_LENGTH.requestorZip, which is the
 * column width and the cap sanitizeZipInput applies to the SEARCH boxes, where Classic also allows
 * 10. Collapsing the two would silently narrow the search path.
 *
 * TWO CONSEQUENCES, stated rather than hidden. A ZIP+4 can no longer be TYPED here - at 5
 * characters there is no keystroke left for "-1234" - exactly as in Classic's entry zip. A
 * PRE-FILLED "14624-1234" is deliberately kept WHOLE: an HTML maxLength caps typing, not a value
 * assigned programmatically, and truncating the extraction's four extra digits would lose them
 * silently, since the shortened value still passes the validator. On the CREATE path that is a real
 * divergence - this field is pre-filled from the extraction and Classic's Add New Requestor form has
 * no extraction feed - and it is kept on the merits (a ZIP+4 is valid by CLASSIC_ZIP_REGEX and the
 * column is varchar(10)), not on parity.
 */
export const CLASSIC_ENTRY_ZIP_MAX_LENGTH = 5;

/**
 * The Patient Info row's single inline field accepts a last name OR an MRN (one field, the
 * search proc takes either), so it has to allow the longer of the two.
 */
export const PATIENT_LAST_NAME_OR_MRN_MAX_LENGTH = Math.max(
    QUICK_ENTRY_FIELD_MAX_LENGTH.patientLastName,
    QUICK_ENTRY_FIELD_MAX_LENGTH.patientMrn,
);

/**
 * AB#35253: the post-hoc rule for a State the dropdown did not offer.
 *
 * The dropdown closes the KEYSTROKE door - a `select` cannot emit a digit - but not the PRE-FILL
 * door, and an unlisted pre-fill is deliberately KEPT and rendered as an option so a requestor
 * stored with a real code the list lacks stays editable (CreateEntityFormModal.initialFieldValue).
 * Kept unconditionally, that handed the same courtesy to an extracted "1L!" - selectable,
 * pre-selected, no error, Save enabled - which is "The State field accepts numeric and special
 * characters", the bug's own wording, over again.
 *
 * Two or three letters. The upper bound is the varchar(3) column (`@State VARCHAR(3)` in
 * QuickEntry_RequestorLookUp_Up.sql), which base carried as `maxLength: 3` on the free-text box; a
 * `select` has no maxLength to carry, so expressing the cap as a rule is what restores it - and
 * reports it, which an HTML maxLength never could for a value nobody typed.
 *
 * Letters-only rather than a membership test against UNITED_STATES: every one of the 55
 * abbreviations is two letters, so an unlisted real code ("ON") still passes and still saves. This
 * rejects malformed values, not unlisted ones. Classic's requestor tab has its own post-hoc State
 * rule - `state.length < 2` -> "Enter valid state." (localValidations.ts:256-258) - and this is
 * stricter than it, on the same reasoning as City above.
 */
export const QUICK_ENTRY_STATE_REGEX = /^[A-Za-z]{2,3}$/;

/**
 * Classic's message for its own State rule is "Enter valid state." This names the rule instead,
 * because our rule is narrower than Classic's length check and the value in the box arrived from an
 * extraction or a record - "wrong how?" would otherwise be a guess.
 */
export const INVALID_STATE_MESSAGE = "State must be a 2- or 3-letter code.";

/**
 * The characters City accepts, split into the LETTER half and the whole allowed set. Two constants
 * so the "at least one letter" lookahead in QUICK_ENTRY_CITY_REGEX cannot drift from the class it
 * guards. QUICK_ENTRY_CITY_REGEX is the only consumer of either; there is no keystroke filter built
 * from them.
 *
 * Two narrowings this set must NOT take, both of which break real data:
 *
 *   - LETTERS ONLY would reject "Coeur d'Alene", "Winston-Salem" and "St. Louis". The punctuation
 *     is data, not noise.
 *   - ASCII LETTERS ONLY would refuse an accented name outright and block Save on a real city. So
 *     Latin-1, Latin Extended-A/B and Latin Extended Additional are all letters here, covering
 *     "Montreal", "Waimanalo" with its macrons and "Lodz".
 *
 * PDF extraction does not spell any of this canonically, which is why the set is this wide: it
 * emits the NFD form (base letter + a combining mark, so U+0304 where U+0101 was expected), a
 * non-breaking space between "New" and "York", U+2018 for the okina in "Ka'a'awa", and U+2010,
 * U+2011 or U+2013 for the hyphen in "Winston-Salem". All of those are the same NAME, and all of
 * them were reported as containing "numbers or special characters" while the set was narrower.
 *
 * The test for admitting anything is that a specialist can SEE it in the box. So U+00A0 is in and
 * the zero-width spaces (U+200B and friends) are out - an invisible character would let a city that
 * looks correct save as a value that silently is not. Likewise U+2010-U+2014 (all visible dashes)
 * are in and U+2015 is out. U+00D7 and U+00F7 are excluded because they are the multiplication and
 * division signs, not letters.
 *
 * THE OKINA AND THE MODIFIER APOSTROPHE (U+02BB, U+02BC) ARE IN THE ALLOWED SET BUT NOT IN THE
 * LETTER HALF. Unicode classes them as modifier letters, so counting them as the "at least one
 * letter" the lookahead demands made a City of nothing but them - "<U+02BB><U+02BB><U+02BB>" - pass
 * with Save enabled, which is the letterless-punctuation hole the lookahead exists to close: in the
 * box they read as apostrophes. They stay in CITY_ALLOWED_CHARACTERS, so "Ka<U+02BB>a<U+02BB>awa"
 * is still accepted - it has real letters of its own.
 *
 * Explicit ranges rather than \p{L}: this bundle targets ES5 on TypeScript 3.9 (tsconfig.json) and
 * no other regex in src carries the `u` flag, so a Unicode property escape would put the fix at the
 * mercy of the transpile chain.
 */
const CITY_LETTERS =
    "A-Za-z" +
    "\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u00FF" +
    "\\u0100-\\u024F" +
    "\\u1E00-\\u1EFF";

const CITY_ALLOWED_CHARACTERS =
    CITY_LETTERS +
    "\\u0300-\\u036F" +     // combining diacriticals - the SAME letters, NFD-decomposed
    "\\u02BB\\u02BC" +      // okina / modifier letter apostrophe - punctuation-like, not a letter
    " \\u00A0" +            // space + NBSP
    "\\u2018\\u2019'" +     // both curly quotes + the straight apostrophe
    "\\u2010-\\u2014\\-" +  // Unicode hyphens/dashes + the ASCII hyphen
    ".";                    // period

/**
 * The whole City rule, applied to every value the form holds - typed or pre-filled.
 *
 * There is NO keystroke transform, deliberately. City had one and it was removed rather than
 * widened a fourth time: three rounds widened the class and a probe on the widened class still found
 * the same defect one block out - "Wilkes<U+2015>Barre" stripped to "WilkesBarre" and
 * "New<U+200B>York" to "NewYork", and because the STRIPPED value satisfies this pattern, no error
 * rendered and Save stayed enabled on a misspelled city. The set of characters a place name can
 * carry is open, so widening moves the boundary without removing it. Cap, validate, never rewrite -
 * the treatment Zip (AB#37973) and Phone (AB#37970) already get here.
 *
 * A pre-filled value - the extraction on the create form, the requestor record on the edit form -
 * reaches component state without passing through any onChange, which is why a keystroke-only rule
 * could never have covered the reported defect: "Springfield, IL 62704" sat in the box, with Save
 * enabled, as the form's DEFAULT state. The typed and pre-filled values are now judged by this one
 * predicate, so they cannot disagree. (An automated single match bypasses both - see the header.)
 *
 * IT REQUIRES AT LEAST ONE LETTER. Without the lookahead the class alone accepted a value made only
 * of the punctuation it admits - ".", "-", "'" or any run of them - so a City that is not a name at
 * all passed with Save enabled. Trailing punctuation on a real name is still accepted ("St. Louis"),
 * because policing punctuation POSITION is a different question.
 *
 * IT BOUNDS LENGTH. `maxLength: 30` caps typing only, so an unbounded pattern left a run-on
 * extraction - the failure mode that produced "Springfield, IL 62704" - unreported and on its way to
 * `@City VARCHAR(30)`, to be truncated at the parameter boundary rather than flagged. The bound is
 * the column width, so the rule and the cap cannot drift.
 *
 * Anchored, so it speaks only about a non-empty value; emptiness is `required`'s business, and
 * CreateEntityFormModal skips `pattern` on an empty field.
 */
export const QUICK_ENTRY_CITY_REGEX = new RegExp(
    "^(?=.*[" + CITY_LETTERS + "])"
    + "[" + CITY_ALLOWED_CHARACTERS + "]{1," + QUICK_ENTRY_FIELD_MAX_LENGTH.requestorCity + "}$",
);

/**
 * Classic has no City rule to quote, so this names ours instead of saying "invalid". Both halves of
 * the rule, built from the cap rather than repeating it: a field carries one `invalidMessage`, and a
 * 31-character city reported only as containing "numbers or special characters" would send the
 * specialist hunting a character that is not there.
 */
export const INVALID_CITY_MESSAGE =
    "City cannot contain numbers or special characters, and is limited to "
    + QUICK_ENTRY_FIELD_MAX_LENGTH.requestorCity + " characters.";

/**
 * AB#35253: Zip accepts digits and the ZIP+4 hyphen, nothing else, capped at 10.
 *
 * Chosen for simplicity over a format validator: strip everything that is not a digit or a
 * hyphen, keep only the FIRST hyphen (so "1-2-3-4-5" cannot accumulate), then cut to 10 - the
 * width of "12345-6789" and of the varchar(10) column. That rejects letters and specials (the
 * reported defect) and admits ZIP+4, without policing hyphen POSITION on every keystroke, which
 * would fight the user mid-type ("12345-" is a legitimate intermediate state). A malformed but
 * in-alphabet value like "1-2345" is therefore still typeable; enforcing the exact 5+4 shape
 * belongs in submit validation, not a keystroke mask.
 *
 * Idempotent: re-running over an already-cleaned value is a no-op, so re-editing never compounds.
 */
export function sanitizeZipInput(raw: string): string {
    const digitsAndHyphens = raw.replace(/[^\d-]/g, "");
    const firstHyphen = digitsAndHyphens.indexOf("-");
    const singleHyphen = firstHyphen === -1
        ? digitsAndHyphens
        : digitsAndHyphens.slice(0, firstHyphen + 1) + digitsAndHyphens.slice(firstHyphen + 1).replace(/-/g, "");
    return singleHyphen.slice(0, QUICK_ENTRY_FIELD_MAX_LENGTH.requestorZip);
}

/**
 * AB#37970 - NO transform of our own, but a post-hoc VALIDATOR (see CLASSIC_PHONE_REGEX below).
 *
 * The bug reported that the Add/Change Contact Phone field accepts more than 10 digits. It does,
 * and so does Classic: `normalizePhone` FORMATS without capping - past 10 digits it emits
 * `XXX-XXX-XXXXxEXT` and keeps every remaining digit - and Classic's own
 * /^\d{3}-\d{3}-\d{4}(x\d{1,8})?$/ in localValidations.ts is a POST-HOC validator that shows
 * the specialist an error message. It never truncates what was typed.
 *
 * An earlier revision of this branch capped the extension at 8 digits here, on the entry-time
 * keystroke pathway. That was stricter than Classic and silently mutated input - the same defect
 * class as the reverted Request ID restriction below: quietly rewriting what someone typed instead
 * of telling them it is wrong. So the "phone" kind delegates straight to Classic's own
 * normalizePhone and nothing else, and the two panes FORMAT identically.
 *
 * WHAT THAT REASONING MISSED, and why this bug was reopened (David Heifetz, PR !9947 thread
 * 53163). Formatting identically is not validating identically. Classic's phone field is a
 * redux-form field, so the regex above runs on it and `465-768-7697x989689879` is refused with
 * "Enter valid phone number."; Quick Entry's Edit Requestor / Add Contact forms are NOT redux-form
 * forms, so nothing ran the regex there and the same value saved silently. Quick Entry was
 * therefore strictly MORE permissive than Classic, which is the reported defect. The fix is the
 * validator, not a cap: same regex, same message, surfaced as an error that gates Save - exactly
 * what AB#37973 does for Bill To Zip.
 */

/**
 * Classic's own contact-phone validator, byte-for-byte - `phoneNumberRegex` in
 * localValidations.ts:234, paired with the message it shows on line 274. Kept as one named
 * constant here (rather than re-typed at the call sites) so the parity is checkable at a glance,
 * the same way BILL_TO_ZIP_REGEX pins the Bill To Zip parity.
 *
 * `x\d{1,8}` is the whole point: Classic accepts an extension of up to eight digits and refuses
 * a ninth, which is what the reported `465-768-7697x989689879` trips. Applied POST-HOC, on the
 * value the specialist finished typing - never as a keystroke cap (AB#37970 above).
 */
export const CLASSIC_PHONE_REGEX = /^\d{3}-\d{3}-\d{4}(x\d{1,8})?$/;

/** The message Classic shows for a phone that fails CLASSIC_PHONE_REGEX (localValidations.ts:274). */
export const INVALID_PHONE_MESSAGE = "Enter valid phone number.";

/**
 * Classic's BILL TO zip validator, byte-for-byte - `zipRegex` in localValidations.ts:336, applied on
 * line 353 to `billZip` and to nothing else, paired with the message on line 354.
 * QuickEntryBillingPane's BILL_TO_ZIP_REGEX aliases it, so Quick Entry's Bill To Zip cannot drift
 * from Classic's. The requestor Zip fields also use it, which is STRICTER than Classic's requestor
 * tab (`zip.length < 5`) - a deliberate divergence, not parity. See the header table.
 */
export const CLASSIC_ZIP_REGEX = /^\d{5}(-\d{4})?$/;

/** The message Classic shows for a zip that fails CLASSIC_ZIP_REGEX (localValidations.ts:354). */
export const INVALID_ZIP_MESSAGE = "Enter valid zip code.";

/**
 * The field kinds a Quick Entry input can declare. "text" (the default), "date" and "select" are
 * inert here - they pick a RENDERER rather than a transform ("date" the shared masked DateField,
 * "select" a dropdown over the field's own `options`) - so only the restricted kinds map to a
 * transform below.
 *
 * There is deliberately NO "requestId" kind - see the REQUEST ID note above.
 */
export type QuickEntryFieldKind = "text" | "date" | "select" | "phone" | "postalCode";

/**
 * Maps a field kind to its keystroke transform. THE single sanitize pathway for Quick Entry
 * input: both CreateEntityFormModal and InlineSearchForm call this, so "phone" masks the same
 * way in the add-new-contact form, the inline edit form and the inline search row.
 *
 * "phone" delegates to the classic pane's own normalizePhone so both panes format identically
 * (AB#35154), including its XXX-XXX-XXXXxEXT extension form past 10 digits, which is deliberately
 * NOT capped (AB#37970 - see the note above) - fax callers cap at 12 (a complete XXX-XXX-XXXX),
 * so a fax box never reaches that form. Every transform is idempotent, so re-editing an
 * already-cleaned value never compounds.
 */
export function sanitizeFieldInput(kind: QuickEntryFieldKind | undefined, raw: string): string {
    switch (kind) {
        case "phone": return normalizePhone(raw);
        case "postalCode": return sanitizeZipInput(raw);
        default: return raw;
    }
}

/**
 * AB#37980: the inline error a Quick Entry "date" field shows when its box holds text the
 * picker could not accept. One constant so both date entry points word it identically.
 *
 * Deliberately NOT a keystroke transform. No keystroke is truncated or rewritten: the box accepts
 * whatever the specialist types (the shared masked DateField auto-inserts the separators) and the
 * entry is judged once, on blur - matching Classic, which validates dates post-hoc and shows a
 * message (DataEntryDetailsRightPane's DOBError) rather than masking as you type.
 *
 * WHERE THIS DOES DIVERGE FROM CLASSIC, said plainly because an earlier version of this comment
 * denied it: on blur, a rejected date is CLEARED out of the form's values map (see
 * InlineSearchForm.onDateFieldBlur / CreateEntityFormModal.onDateFieldBlur). That clear is the
 * load-bearing half of the fix - it is what stops a date the specialist overtyped from being
 * searched or saved as if they had typed it - and because DateField is a controlled component, the
 * box empties along with the value whenever the value actually changed. So the specialist sees an
 * empty box plus this message where Classic would leave their text in place with a message beside
 * it. Kept that way rather than decoupling display from value, which would mean changing shared
 * DateField for every pane that uses it; the error is shown in both cases, so nothing is silent.
 * Pinned by the e2e's afterBlur assertion and by the "no stale pre-fill searched/saved" tests.
 */
export const INVALID_DATE_MESSAGE = "Enter a valid date (MM/DD/YYYY)";

/**
 * DateField emits a Date; the Quick Entry forms' values maps are string-keyed (MM/DD/YYYY, fed
 * to toWireDob). Normalize back to that string form.
 *
 * AB#37980: shared by InlineSearchForm AND CreateEntityFormModal. It used to be a byte-identical
 * private copy in each, which is precisely how this bug's first fix landed in only one of the two
 * DOB entry points - one function here means the next date fix cannot half-land again.
 *
 * The year is zero-padded: without the pad a year under 1000 (typing "01010001" masks to
 * 01/01/0001, which the picker accepts) produced "01/01/1", which toWireDob passes through and
 * the DateOnly-bound server rejects with a 400 - surfacing to the specialist as a spurious
 * "Match service unavailable" rather than anything about their date.
 */
export function dateFieldValueToString(v?: Date | string): string {
    if (!v) {
        return "";
    }
    if (typeof v === "string") {
        return v === INVALID_DATE ? "" : v;
    }
    if (v instanceof Date && !isNaN(v.getTime())) {
        const mm = `${v.getMonth() + 1}`.padStart(2, "0");
        const dd = `${v.getDate()}`.padStart(2, "0");
        return `${mm}/${dd}/${`${v.getFullYear()}`.padStart(4, "0")}`;
    }
    return "";
}

/**
 * AB#38460 (David Heifetz, PR !10140 review): is an unusable entry out of RANGE, as opposed to
 * merely not a real date?
 *
 * The two deserve different messages and the distinction is not obvious, so it lives here once:
 *
 *   - "02/30/1996", "04/31/2000", "02/29/1997" - month and day are within their nominal bounds
 *     and the year is inside the SQL range; the COMBINATION just does not exist. Telling the
 *     specialist it falls outside 1753-9999 is simply false - 1996 does not.
 *   - "99/99/9999" - month 99 and day 99 are outside any month/day can be. (Note 9999 itself IS
 *     inside the SQL range, so "out of range" here is about the month/day, not the year.)
 *   - "01/01/1600" - a perfectly real date, but before SQL Server's datetime floor.
 *
 * Returns true for the second and third, false for the first and for anything incomplete. Callers
 * map true to the range message and false to INVALID_DATE_MESSAGE.
 */
export function isOutOfRangeDateEntry(text: string): boolean {
    // Shape first, via the existing predicate rather than a second copy of its regex.
    if (!hasCompleteDateShape(text)) {
        // Incomplete - not a range problem, just unfinished.
        return false;
    }
    const [month, , year] = text.trim().split("/").map(Number);
    // AB#38460: the DAY is deliberately not checked here. Any day problem - 40, 00, or a 31 in a
    // month that has no 31st - is "not a real date" rather than "out of range", and gets
    // INVALID_DATE_MESSAGE via isCompleteCalendarDate instead. Only the MONTH and the YEAR are
    // range questions: 03/40/2026 was being told it fell outside 1753-9999, which 2026 does not.
    if (month < 1 || month > 12) {
        return true;
    }
    // SQL Server datetime floor/ceiling, matching isDateNotSqlOverflow.
    return year < 1753 || year > 9999;
}

/**
 * AB#38460: has a full MM/DD/YYYY been TYPED - regardless of whether it names a real day.
 *
 * The shape half of isCompleteCalendarDate below, split out because the two questions have
 * different answers and different consequences:
 *   - "99/99/9999" is complete but unreal    -> say so; the specialist has finished and is wrong.
 *   - "99/99/999" / "12/31/202" is unfinished -> do not nag mid-keystroke, but do NOT let it be
 *     confirmed either. Clicking Confirm never blurs (the button preventDefaults its mousedown),
 *     so an unfinished entry could otherwise be confirmed and silently keep the previous value.
 */
export function hasCompleteDateShape(text: string): boolean {
    return /^\d{2}\/\d{2}\/\d{4}$/.test(text.trim());
}

/**
 * AB#37980: true only when `text` is a COMPLETE, REAL calendar date in MM/DD/YYYY.
 *
 * This exists because DateField's two channels into onChange both stay silent for a
 * complete-looking but non-existent date, so neither the value nor an error is ever produced:
 *
 *   1. the masked (rifm) picker's own onChange - date-fns strict `parse` rejects "02/30/1996",
 *      so DateField's `!isNaN(date.getTime())` guard means onChange is never called at all;
 *   2. DateField's onBlur - which only reports INVALID_DATE when `new Date(text)` is NaN, and
 *      `new Date("02/30/1996")` is NOT NaN: it ROLLS OVER to 1996-03-01.
 *
 * So "02/30/1996", "04/31/2000" and "02/29/1997" (a non-leap year - a common mis-transcription
 * off a faxed authorization) each left the box displaying a filled-in date with no error while
 * the form held no date, or worse still held the previous AI-extracted pre-fill. `new Date()` is
 * therefore not usable as the check; this compares the parsed parts against a round-trip so a
 * rolled-over day fails.
 *
 * Rejects an incomplete masked entry too ("10/22/19__", "10/22/") via the shape regex, so one
 * predicate covers every class the blur handler can see.
 */
export function isCompleteCalendarDate(text: string): boolean {
    // AB#38460: shape via the existing predicate rather than a second copy of its regex - the
    // two used to declare the same pattern independently, so tightening one would have silently
    // left the other behind.
    if (!hasCompleteDateShape(text)) {
        return false;
    }
    const [month, day, year] = text.trim().split("/").map(Number);
    // Midday base so no DST boundary can shift the round-trip by a day. setFullYear (not the
    // Date(y, m, d) constructor) because the constructor maps years 0-99 onto 1900-1999, which
    // would fail the round-trip for a legitimately 4-digit-typed "0042".
    const probe = new Date(2000, 0, 1, 12, 0, 0, 0);
    probe.setFullYear(year, month - 1, day);
    return probe.getFullYear() === year
        && probe.getMonth() === month - 1
        && probe.getDate() === day;
}
